import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { unApi } from '../../shared/common/api/endpoints/reports/unApi';
import { showSuccessMessage, showErrorMessage, getApiErrorMessage } from './ui';

interface MissingGroup {
    group: string;
    id: number;
}

const DELETED_GROUPS_CODE = 255;

/**
 * Признак ответа «отчёт ссылается на удалённые группы регистраций».
 * Внимание: ответ приходит с HTTP-статусом 200, признак — поле code в теле (255).
 */
export const isDeletedGroupsError = (data: any): boolean =>
    !!data && (data.code === DELETED_GROUPS_CODE || data.error === 'report_has_deleted_groups');

/**
 * Имя группы по dataKey: сначала ищем в локальном справочнике групп,
 * иначе показываем номер (например для чужого отчёта, где группы нет локально).
 */
const resolveGroupName = (groupKey: string, id: number, pool?: any[]): string => {
    const found = Array.isArray(pool)
        ? pool.find((item) => item && (item.dataKey === groupKey || item.key === groupKey))
        : undefined;
    return found?.name || found?.label || `#${id}`;
};

/**
 * Локальный справочник групп из Redux (customGroups) — для резолва имён.
 * Берётся лениво, чтобы не тянуть store статически в компоненты отчёта.
 */
const getGroupsPoolFromStore = async (): Promise<any[]> => {
    try {
        const { store } = await import('../../app/providers/redux/store');
        const state: any = store.getState();
        return state?.table?.customGroups?.[0]?.children ?? [];
    } catch (e) {
        return [];
    }
};

/**
 * Убирает «мёртвые» оси-группы (dataKey из missingKeys) из выбранных группировок
 * в Redux (state.table.groupings + пер-блочные), чтобы удалённая ось не вернулась
 * после починки отчёта.
 */
export const purgeDeletedGroupsFromRedux = async (missingKeys: string[]): Promise<void> => {
    if (!Array.isArray(missingKeys) || missingKeys.length === 0) return;
    try {
        const [{ store }, { tableSliceActions }] = await Promise.all([
            import('../../app/providers/redux/store'),
            import('../../app/providers/redux/slices/reports/table/tableSlice'),
        ]);

        const dead = new Set(missingKeys);
        const state: any = store.getState();

        const groupings: any[] = state?.table?.groupings ?? [];
        const filtered = groupings.filter((g: any) => !dead.has(g?.dataKey));
        if (filtered.length !== groupings.length) {
            store.dispatch(tableSliceActions.setGroupings({ data: filtered, blockId: null }));
        }

        const blocks: Record<string, any> = state?.table?.blocks ?? {};
        Object.keys(blocks).forEach((blockId) => {
            const blockGroupings: any[] = blocks[blockId]?.groupings ?? [];
            const blockFiltered = blockGroupings.filter((g: any) => !dead.has(g?.dataKey));
            if (blockFiltered.length !== blockGroupings.length) {
                store.dispatch(tableSliceActions.setGroupings({ data: blockFiltered, blockId }));
            }
        });
    } catch (e) {
        // состояние недоступно — пропускаем
    }
};

/**
 * Показывает модалку «отчёт ссылается на удалённые группы» со списком групп и
 * кнопкой починки. Вызывается из обработчиков ответа рендера при code === 255.
 *
 * @param data                Тело ответа бэкенда (с missing_groups)
 * @param options.groupsPool  Локальный пул групп для резолва имени (иначе берётся из store)
 * @param options.onRepaired  Колбэк после успешной починки (перечитать/перестроить отчёт)
 */
export const handleDeletedGroupsError = async (
    data: any,
    options?: { groupsPool?: any[]; onRepaired?: () => void }
): Promise<void> => {
    const missing: MissingGroup[] = (data && data.missing_groups) || [];
    const pool = options?.groupsPool || (await getGroupsPoolFromStore());

    const lines = missing.map((item, index) => {
        const name = resolveGroupName(item.group, item.id, pool);
        return (
            <li key={index}>
                <b>{name}</b> (удалена)
            </li>
        );
    });

    // Ключи «мёртвых» осей этого отчёта — для очистки Redux после починки
    const deadKeys = missing.map((item) => item.group || `custom_group_${item.id}`);

    Modal.confirm({
        title: 'Отчёт ссылается на удалённые группы',
        icon: <ExclamationCircleOutlined />,
        width: 560,
        content: (
            <div>
                <p>
                    Некоторые группы регистраций удалены, но остались осью в отчёте, поэтому он не
                    строится:
                </p>
                <ul style={{ marginBottom: 12 }}>{lines}</ul>
                <p>
                    «Починить» уберёт ссылки на удалённые группы из отчётов школы. Сами отчёты не
                    удаляются.
                </p>
            </div>
        ),
        okText: 'Починить отчёты',
        cancelText: 'Закрыть',
        onOk: () =>
            unApi
                .repairCustomGroups()
                .then((res) => {
                    // Ответ починки в конверте { success, code, message, data: { cleaned_reports } }
                    const envelope = res?.data || {};
                    const result = envelope.data ?? envelope;
                    // Чистим текущий выбор отчёта (Redux) от удалённых осей
                    purgeDeletedGroupsFromRedux(deadKeys);
                    showSuccessMessage(
                        `Починка выполнена, исправлено отчётов: ${result.cleaned_reports ?? 0}`
                    );
                    if (options?.onRepaired) {
                        options.onRepaired();
                    }
                })
                .catch((e) => {
                    showErrorMessage(getApiErrorMessage(e, 'Не удалось выполнить починку. Повторите попытку позже.'));
                }),
    });
};
